import React, {useCallback, useEffect, useState, useRef, useContext } from "react";
import "../css/card.css"
import "../css/shake.css"
import AppContext from '../context/AppContext'
import HistoryContext from '../context/HistoryContext'
import CardsContext from '../context/CardsContext'
import ParamsContext from '../context/ParamsContext'
import StatusBar from './StatusBar'
import FlipCard from './FlipCard'
import appendAndClear from '../io/appendAndClear'
import {DEFAULT_PARAMS, writeParamsSheet} from '../io/paramsSheet'
import {
  computeEasyThresholdSecondsFromHistory,
  deriveFsrsRating,
} from "../model/fsrsReviewRating";
import {
  applyPersistedCardSheetState,
  FLAG_STATUS_FLAGGED,
  FLAG_STATUS_NONE,
  FLAG_STATUS_REVERSE_REQUIRED,
  hasCardSheetStateChanged,
  updateCardContent,
  updateCardSheetState,
} from "../io/cardsSheet";

function assert(condition, message) {
  if (!condition) {
    throw new Error(message || "Assertion failed");
  }
}

const hasTruthyQueryValue = (value) =>
  value != null && ["1", "true", "yes", "on"].includes(String(value).toLowerCase());

const shouldProfileSubmitPath = () => {
  try {
    return hasTruthyQueryValue(
      new URLSearchParams(window.location.search).get("profile_submit")
    );
  } catch (error) {
    return false;
  }
};

const createSubmitProfiler = (enabled, label) => {
  if (!enabled || typeof performance === "undefined") {
    return {
      mark() {},
      flush() {},
    };
  }

  const startedAt = performance.now();
  let lastMarkAt = startedAt;
  const rows = [];

  return {
    mark(step) {
      const now = performance.now();
      rows.push({
        step,
        ms_since_last: Number((now - lastMarkAt).toFixed(2)),
        ms_total: Number((now - startedAt).toFixed(2)),
      });
      lastMarkAt = now;
    },
    flush(extra = {}) {
      console.groupCollapsed(`[submit-profile] ${label}`);
      console.table(rows);
      console.log(extra);
      console.groupEnd();
    },
  };
};

const FLY_OFF_DURATION_MS = 220;
const INCORRECT_REVEAL_MS = 450;

const getLocalMidnightEpochSeconds = () => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  return currentDate.getTime() / 1000;
};

const countReviewsSinceEpoch = (history, epochSeconds) =>
  Object.keys(history).reduce(
    (total, cardId) =>
      total +
      history[cardId].reduce(
        (cardTotal, reviewRow) =>
          reviewRow.submitted_time >= epochSeconds ? cardTotal + 1 : cardTotal,
        0
      ),
    0
  );

const buildReviewMetadata = (historyRows, cards) => {
  const reverseRequiredByCardId = new Map(
    (cards || []).map((currentCard) => [
      String(currentCard.card_id),
      !!currentCard.reverseRequired,
    ])
  );

  return (historyRows || []).map((historyRow) => ({
    reverse_required:
      historyRow.reverse_required ??
      reverseRequiredByCardId.get(String(historyRow.card_id)) ??
      false,
    selection_mode: historyRow.selection_mode ?? null,
  }));
};

export const Card = () => {
  const {cardSelector, userGuid, spreadsheetAccessData} = useContext(AppContext)                                                 
  const spreadsheetId = spreadsheetAccessData['spreadsheetId'];
  const {cards, setCards, pCorrect, setPCorrect, preSelectedCard} = useContext(CardsContext)
  // eslint-disable-next-line
  const {params, setParams} = useContext(ParamsContext)                                              
  const {history, setHistory, review, setReview} = useContext(HistoryContext)
  const [sessionReviews, setSessionReviews] = useState(1);

  const [isFront, setIsFront] = useState(1);
  const [flipped, setFlipped] = useState(false); 
  //const [flippingCss, setFlippingCss] = useState("");

  const [card, setCard] = useState(preSelectedCard);
  
  const [showedTime, setShowedTime] = useState( Date.now() ); // utc epoch time
  const [flippedTime, setFlippedTime] = useState(null);
  const [lastAppendTime, setLastAppendTime] = useState(null);
  //const [hasPendingUpdate, setHasPendingUpdate] = useState(false);
  const [shake, setShake] = useState(false);
  const [bothSides, setBothSides] = useState(
    params.showBothSides ?? DEFAULT_PARAMS.showBothSides
  );
  const [useRandomOrder, setUseRandomOrder] = useState(
    params.useRandomOrder ?? DEFAULT_PARAMS.useRandomOrder
  );
  // to add an animation
  const [flyOff, setFlyOff] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [suppressIncomingFlipTransition, setSuppressIncomingFlipTransition] = useState(false);
  const [integralDelta, setIntegralDelta] = useState(0);
  const [halfLife, setHalfLife] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState({
    front: "",
    frontAdditional: "",
    back: "",
    backAdditional: "",
  });
  const historyStack = useRef([]);
  const uploadInFlightRef = useRef(false);
  const flippedTimeRef = useRef(null);
  const activeFlipOccurredRef = useRef(false);
  const cardRef = useRef(preSelectedCard);
  const cardsRef = useRef(cards);
  const profileSubmitPath = useRef(shouldProfileSubmitPath());
  const sessionEasyThresholdRef = useRef(undefined);
  const [totalToday, setTotalToday ] = useState(
    countReviewsSinceEpoch(history, getLocalMidnightEpochSeconds())
  );
  const [touchStartX, setTouchStartX] = useState(null)
  const [touchStartY, setTouchStartY] = useState(null)
  const [touchEndX, setTouchEndX] = useState(null)
  // const [touchEndY, setTouchEndY] = useState(null)
  // the required distance between touchStartX and touchEndX to be detected as a swipe
  const minSwipeDistance = 50 

  useEffect(() => {
    setBothSides(params.showBothSides ?? DEFAULT_PARAMS.showBothSides);
    setUseRandomOrder(params.useRandomOrder ?? DEFAULT_PARAMS.useRandomOrder);
  }, [params.showBothSides, params.useRandomOrder]);

  useEffect(() => {
    cardRef.current = card;
  }, [card]);

  useEffect(() => {
    setEditMode(false);
    setEditValues({
      front: card?.front ?? "",
      frontAdditional: card?.frontAdditional ?? "",
      back: card?.back ?? "",
      backAdditional: card?.backAdditional ?? "",
    });
  }, [card?.card_id, card?.front, card?.frontAdditional, card?.back, card?.backAdditional]);

  useEffect(() => {
    cardsRef.current = cards;
  }, [cards]);

  useEffect(() => {
    if (sessionEasyThresholdRef.current !== undefined) {
      return;
    }
    if (Object.keys(history || {}).length === 0) {
      return;
    }
    sessionEasyThresholdRef.current = computeEasyThresholdSecondsFromHistory(history);
    console.log(
      `[fsrs] easy_threshold_seconds=${
        sessionEasyThresholdRef.current == null
          ? "null"
          : Number(sessionEasyThresholdRef.current.toFixed(4))
      }`
    );
  }, [history]);

  const persistParams = useCallback(
    async (nextParams) => {
      setParams(nextParams);
      try {
        await writeParamsSheet(spreadsheetAccessData, nextParams);
      } catch (error) {
        console.error('Failed to persist params', error);
      }
    },
    [spreadsheetAccessData, setParams]
  );

  useEffect(() => {
    cardSelector.setParams(params, persistParams);
  }, [cardSelector, params, persistParams]);

  const handleSetBothSides = async (nextValue) => {
    const nextParams = {
      ...DEFAULT_PARAMS,
      ...params,
      showBothSides: nextValue,
      useRandomOrder,
    };
    setBothSides(nextValue);
    await persistParams(nextParams);
  };

  const handleSetUseRandomOrder = async (nextValue) => {
    const nextParams = {
      ...DEFAULT_PARAMS,
      ...params,
      showBothSides: bothSides,
      useRandomOrder: nextValue,
    };
    setUseRandomOrder(nextValue);
    await persistParams(nextParams);
  };

  const onTouchStart = (e) => {
    if (editMode) {
      return;
    }
    setFlyOff(null);
    setTouchEndX(null) // otherwise the swipe is fired even with usual touch events
    // setTouchEndY(null) // otherwise the swipe is fired even with usual touch events
    setTouchStartX(e.targetTouches[0].clientX)
    setTouchStartY(e.targetTouches[0].clientY)
  }

  const dragXY = useRef([0, 0]);
  
  const onTouchMove = (e) => {
    if (editMode) {
      return;
    }
    if (touchStartX == null || touchStartY == null) {
      return;
    }
    dragXY.current[0] = e.targetTouches[0].clientX - touchStartX;
    dragXY.current[1] = e.targetTouches[0].clientY - touchStartY;
    setTouchEndX(e.targetTouches[0].clientX)
    //setTouchEndY(e.targetTouches[0].clientY)
  }

  const onTouchEnd = () => {
    if (editMode) {
      return;
    }
    if (touchStartX == null || touchEndX == null) {
      return;
    }
    const distanceX = touchStartX - touchEndX
    // const distanceY = touchStartY - touchEndY
    const isLeftSwipe = distanceX > minSwipeDistance
    const isRightSwipe = distanceX < -minSwipeDistance
    if (isLeftSwipe ) {
      getCardInternal(0);
    } else if (isRightSwipe ) {
      getCardInternal(1);
    } else {
      dragXY.current = [0,0];
    }
    setTouchStartX(null);
    setTouchStartY(null);
    setTouchEndX(null);
  } 
  
  const onKeyPress = (e) => {
    if (e.repeat) {
      return;
    }

    if (editMode) {
      if (e.key === "Escape") {
        e.preventDefault();
        setEditMode(false);
        setEditValues({
          front: cardRef.current?.front ?? "",
          frontAdditional: cardRef.current?.frontAdditional ?? "",
          back: cardRef.current?.back ?? "",
          backAdditional: cardRef.current?.backAdditional ?? "",
        });
      }
      return;
    }
    if (e.key === "ArrowLeft" ) {
      e.preventDefault();
      getCardInternal(0);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      flip();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      getCardInternal(1)
    } else if (e.key === "Backspace") {
      e.preventDefault();
      getCardInternal(0)
    } else if (e.key === "Enter") {
      e.preventDefault();
      getCardInternal(1)
    } else if (e.key === " ") {
      e.preventDefault();
      flip();
    }
  }
  const recordFlipTime = () => {
    if (flippedTimeRef.current != null) {
      return;
    }
    const nextFlipTime = Date.now() / 1000;
    flippedTimeRef.current = nextFlipTime;
    setFlippedTime(nextFlipTime);
  };

  const flip = () => {
    if (isTransitioning || editMode) {
      return;
    }
    activeFlipOccurredRef.current = true;
    recordFlipTime();
    setIsFront ( x => (! x))
    setFlipped(true);
  }

  const revealReverse = () => {
    if (isTransitioning || !isFront || editMode) {
      return;
    }
    dragXY.current = [0,0];
    recordFlipTime();
    setIsFront(false);
    setFlipped(true);
  }
  
  // useEffect to check myList length and time elapsed
  useEffect(() => {
    const forceReloadOnUploadError = () => {
      window.location.reload();
    };

    const uploadPendingHistory = async (existingHistory, fullExistingHistory) => {
      if (uploadInFlightRef.current) {
        return;
      }
      uploadInFlightRef.current = true;
      try {
        const success = await appendAndClear(
          existingHistory,
          spreadsheetAccessData,
          userGuid,
          buildReviewMetadata(existingHistory, cardsRef.current)
        );
        if (!success) {
          forceReloadOnUploadError();
          return;
        }
        historyStack.current = [];
        window.localStorage.setItem('existingHistory', JSON.stringify({
          ...fullExistingHistory,
          [spreadsheetId]: []
        }));
      } finally {
        uploadInFlightRef.current = false;
      }
    };

    // Define a function to check the conditions and call func if needed
    const checkConditionsAndRunFunc = async () => {
      let fullExistingHistory = {}
      try {
        fullExistingHistory = JSON.parse(window.localStorage.getItem('existingHistory')) 
      }
      catch (e ) {
        console.log('fullExistingHistory is empty')  
      }
      if (! fullExistingHistory) return;
      let existingHistory = fullExistingHistory[spreadsheetId]
      if (! existingHistory) return;
      if (existingHistory.length > 0 && existingHistory.length % 10 === 0) {
        console.log('trying append');
        await uploadPendingHistory(existingHistory, fullExistingHistory);
      }
      else if (historyStack.current.length > 0 && (lastAppendTime && Date.now() - lastAppendTime >= 60000)) {
        await uploadPendingHistory(existingHistory, fullExistingHistory);
        setLastAppendTime(Date.now());
      }
    };

    // Run the checkConditionsAndRunFunc function initially
    void checkConditionsAndRunFunc();

    // Set up a timer to periodically check conditions
    const timer = setInterval(() => {
      void checkConditionsAndRunFunc();
    }, 1000); // Check every second

    // Clean up the timer when the component unmounts
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [historyStack, lastAppendTime, appendAndClear]);

  const getCard = async (was_correct, event_) => {
    if (isTransitioning || editMode) {
      return;
    }
    event_.stopPropagation()
    return getCardInternal(was_correct);
  } 
  const animateFlyOff = (was_correct) => {
    const currentX = dragXY.current[0] || 0;
    const currentY = dragXY.current[1] || 0;
    const direction = currentX !== 0 ? Math.sign(currentX) : (was_correct ? 1 : -1);
    const viewportWidth = window.innerWidth || 400;
    const targetX = currentX + direction * (viewportWidth + 200);
    const deltaX = targetX - currentX;
    const slope = currentX !== 0 ? currentY / currentX : 0;
    const targetY = currentY + slope * deltaX;
    const rotation = currentX * currentX / 175 / 175 * 10;
    setFlyOff({
      targetX,
      targetY,
      rotation,
    });
  };

  const getCardInternal = async (was_correct) => {
    if (editMode) {
      return;
    }
    const currentCard = cardRef.current;
    if (isFront && currentCard?.reverseRequired) {
      revealReverse();
      return;
    }

    if (isTransitioning) {
      return;
    }

    const profiler = createSubmitProfiler(
      profileSubmitPath.current,
      `card=${cardRef.current?.card_id} answer=${was_correct ? "yes" : "no"}`
    );
    profiler.mark("submit_clicked");
    setIsTransitioning(true);
    try {
      const shouldRevealReverseBeforeAdvance = isFront && !was_correct;

      if (shouldRevealReverseBeforeAdvance) {
        setIsFront(false);
        dragXY.current = [0,0]
        setFlipped(true);
        recordFlipTime();
        await new Promise((resolve) =>
          setTimeout(resolve, INCORRECT_REVEAL_MS)
        );
        profiler.mark("reverse_reveal_wait_complete");
      }

      animateFlyOff(was_correct);
      profiler.mark("flyoff_state_set");
      await handleNext(was_correct, profiler);
      profiler.mark("handle_next_complete");
      profiler.flush({
        reverseRequired: !!currentCard?.reverseRequired,
        wasCorrect: !!was_correct,
      });
    } catch (error) {
      setFlyOff(null);
      setIsTransitioning(false);
      profiler.flush({
        error: String(error),
      });
      throw error;
    }
  }
  const [sideToShow, setSideToShow] = useState(
    !!cardSelector?.lastSelection?.sideShown
  );

  const updateCardState = (cardId, patch) => {
    let nextCardRecord = null;
    const nextCards = (cardsRef.current ?? []).map((existingCard) => {
      if (String(existingCard.card_id) !== String(cardId)) {
        return existingCard;
      }
      nextCardRecord = {
        ...existingCard,
        ...patch,
      };
      return nextCardRecord;
    });

    cardsRef.current = nextCards;
    setCards(nextCards);

    if (String(cardRef.current?.card_id) === String(cardId)) {
      const updatedCard = {
        ...cardRef.current,
        ...patch,
      };
      cardRef.current = updatedCard;
      nextCardRecord = updatedCard;
      setCard(updatedCard);
    }

    return nextCardRecord;
  };

  const persistCardSheetStateIfChanged = (
    previousCard,
    nextCard,
    { includeActive = false, errorMessage = "Failed to persist card sheet state" } = {}
  ) => {
    if (
      !hasCardSheetStateChanged(previousCard, nextCard, {
        includeActive,
      })
    ) {
      return;
    }

    updateCardSheetState(spreadsheetAccessData, nextCard, {
      includeActive,
    })
      .then(() => {
        updateCardState(
          nextCard.card_id,
          applyPersistedCardSheetState(nextCard, {
            includeActive,
          })
        );
      })
      .catch((error) => {
        console.error(errorMessage, error);
    });
  };

  const toggleMuted = () => {
    if (editMode) {
      return;
    }
    const currentCard = cardRef.current;
    updateCardState(currentCard.card_id, {
      muted: !currentCard.muted,
    });
  };

  const cycleFlagStatus = () => {
    if (editMode) {
      return;
    }
    const currentCard = cardRef.current;
    const nextFlagStatus =
      currentCard.flagStatus === FLAG_STATUS_NONE
        ? FLAG_STATUS_FLAGGED
        : currentCard.flagStatus === FLAG_STATUS_FLAGGED
          ? FLAG_STATUS_REVERSE_REQUIRED
          : FLAG_STATUS_NONE;
    updateCardState(currentCard.card_id, {
      flagStatus: nextFlagStatus,
      flagged: nextFlagStatus === FLAG_STATUS_FLAGGED,
      reverseRequired: nextFlagStatus === FLAG_STATUS_REVERSE_REQUIRED,
    });
  };

  useEffect(() => {
    if (card?.card_id == null || Number(card.card_id) < 0 || card.active) {
      return;
    }

    const introducedCard = {
      ...card,
      active: true,
    };
    updateCardState(card.card_id, {
      active: true,
    });
    persistCardSheetStateIfChanged(card, introducedCard, {
      includeActive: true,
      errorMessage: "Failed to persist introduced card state",
    });
    // Only new introductions should write the active column from this component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.card_id]);

  const handleNext = async (was_correct, profiler = createSubmitProfiler(false)) => {
    const currentCard = cardRef.current;
    // number of cards in the stack already

    const nth = (history[currentCard.card_id] && history[currentCard.card_id].length) || 0;
    const lastReviewElement = nth > 0 ? history[currentCard.card_id][nth-1] : undefined
    /* side 0 or side 1 is front or back respectively */
    const frontCount = history[currentCard.card_id] ? history[currentCard.card_id].filter((x, i) => ((! x.side_shown) || x.side_shown === undefined)).length : 0;
    const backCount = history[currentCard.card_id] ? history[currentCard.card_id].filter((x, i) => x.side_shown ).length : 0;
    if (sessionEasyThresholdRef.current === undefined) {
      sessionEasyThresholdRef.current = computeEasyThresholdSecondsFromHistory(history);
      console.log(
        `[fsrs] easy_threshold_seconds=${
          sessionEasyThresholdRef.current == null
            ? "null"
            : Number(sessionEasyThresholdRef.current.toFixed(4))
        }`
      );
    }
    const submittedTimeSeconds = Date.now() / 1000;
    const visibleTimeSeconds = submittedTimeSeconds - showedTime / 1000;
    const rating = deriveFsrsRating({
      wasCorrect: was_correct,
      wasActivelyFlipped: activeFlipOccurredRef.current,
      visibleTimeSeconds,
      easyThresholdSeconds: sessionEasyThresholdRef.current,
    });
    const newReviewElement =       {
        'side_shown': !!sideToShow,
        'both_sides_mode': bothSides,
        'card_id': currentCard.card_id,                                                                     
        'submitted_time': submittedTimeSeconds,
        'showed_time': showedTime / 1000,  
        'flipped_time': flippedTimeRef.current ?? flippedTime,
        'review': review,                                                                            
        'was_flipped': flipped,                                
        'delta_t': (nth > 0 && submittedTimeSeconds - history[currentCard.card_id][nth - 1].submitted_time) || 0,
        'delta_r': (nth > 0 && review - history[currentCard.card_id][nth - 1].review) || 0,
        'visible_time': visibleTimeSeconds,
        'last_correct': nth > 0 ? !!history[currentCard.card_id][nth - 1].was_correct : false,
        // number correct before this update plus whether the last was correct: so that on the next review, we have the total number correct so far
        'correct':    (nth > 0 && lastReviewElement.correct + lastReviewElement.was_correct) || 0,
        'incorrect':  (nth > 0 && lastReviewElement.incorrect + 1 - lastReviewElement.was_correct) || 0,
        'is_new': nth === 0,
        'model_version': cardSelector.getModelVersion(),
        'p_correct': pCorrect,
        'reverse_required': !!currentCard.reverseRequired,
        'selection_mode': cardSelector?.lastSelection?.selectionMode ?? null,
        'rating': rating,
        'was_correct': was_correct,
        'last_ten': nth > 0 ? history[currentCard.card_id].map((x, i) => (i > nth - 10 - 1) ? x.was_correct : 0).reduce((a,r) => r+a, 0)
            / history[currentCard.card_id].filter((x,i) => (i > nth - 10 - 1)).length : 0,
        'front_prob': frontCount > 0 ? history[currentCard.card_id].map((x, i) => ((! x.side_shown) || x.side_shown === undefined) && x.was_correct).reduce((a,r) => r+a, 0) / frontCount : 0,
        'back_prob': backCount > 0 ? history[currentCard.card_id].map((x, i) => x.side_shown && x.was_correct).reduce((a,r) => r+a, 0) / backCount: 0
      };
    profiler.mark("review_payload_built");
    historyStack.current.push(newReviewElement);
    // primary storage is to window.localStorage in case we get disconnected
    let fullExistingHistory = {};
    try {
      fullExistingHistory = JSON.parse(window.localStorage.getItem('existingHistory'))
    } catch (e) {
      console.log('could not update fEH');
    }
    window.localStorage.setItem('existingHistory', JSON.stringify({
      ...fullExistingHistory,
        [spreadsheetId]: historyStack.current
    }))
    profiler.mark("local_storage_written");
    setLastAppendTime(Date.now());
    let newHistory = {};
    let newHistorySequence = currentCard.card_id in history ? [...history[currentCard.card_id], newReviewElement] : [newReviewElement];
    newHistory = {
        ...history,
        [currentCard.card_id]: newHistorySequence
      };
    
    setHistory({...newHistory})
    profiler.mark("history_state_updated");
    console.log(
      `[review-submit] card=${Number(newReviewElement.card_id)} side=${
        newReviewElement.side_shown ? "back" : "front"
      } was_correct=${Boolean(newReviewElement.was_correct)} review=${
        Number(newReviewElement.review)
      } rating=${Number(newReviewElement.rating)} both_sides=${Boolean(
        newReviewElement.both_sides_mode
      )} is_front=${Boolean(
        isFront
      )}`
    );
    
    await cardSelector.setSingleHistoryElement(newHistorySequence, currentCard.card_id);
    profiler.mark("selector_history_updated");
    const fsrsScheduled = cardSelector?.lastScheduledFsrsState ?? null;
    if (fsrsScheduled?.review === newReviewElement && fsrsScheduled?.state?.dueAt != null) {
      const submittedTime = Number(newReviewElement.submitted_time ?? 0);
      const dueAt = Number(fsrsScheduled.state.dueAt);
      const intervalSeconds = Math.max(dueAt - submittedTime, 0);
      const wholeDays = Math.floor(intervalSeconds / 86400);
      const wholeHours = Math.floor((intervalSeconds % 86400) / 3600);
      const wholeMinutes = Math.floor((intervalSeconds % 3600) / 60);
      console.log(
        `[fsrs] reviewed card=${Number(newReviewElement.card_id)} side=${
          newReviewElement.side_shown ? "back" : "front"
        } phase=${fsrsScheduled.state.phase ?? "unknown"} reason=${
          fsrsScheduled.state.schedulingReason ?? "unknown"
        } due_at=${Number(dueAt.toFixed(3))} breakdown=${wholeDays}d${wholeHours}h${wholeMinutes}m`
      );
    }
    cardSelector.recordSessionReviewEvent({
      cardId: currentCard.card_id,
      pCorrect,
      wasCorrect: was_correct,
    });

    const currentCardState = {
      ...currentCard,
      active: true,
      muted: !!currentCard.muted,
      flagStatus: currentCard.flagStatus ?? FLAG_STATUS_NONE,
      flagged: (currentCard.flagStatus ?? FLAG_STATUS_NONE) === FLAG_STATUS_FLAGGED,
      reverseRequired:
        (currentCard.flagStatus ?? FLAG_STATUS_NONE) === FLAG_STATUS_REVERSE_REQUIRED,
    };
    updateCardState(currentCard.card_id, currentCardState);
    persistCardSheetStateIfChanged(currentCard, currentCardState);
    profiler.mark("card_state_updated");

    const nextCards = cardsRef.current.map((existingCard) =>
      String(existingCard.card_id) === String(currentCard.card_id)
        ? currentCardState
        : existingCard
    );
    cardsRef.current = nextCards;
    cardSelector.cards = nextCards;
    profiler.mark("selector_cards_swapped");

    let {
      probability: p_correct_est,
      cardId : next,
      sideShown: nextSideShown,
    } = await cardSelector.selectCard(review + 1, currentCard.card_id, sideToShow, bothSides);
    profiler.mark("selector_next_card_selected");
    const nextCard = nextCards.filter(
      (existingCard) => existingCard.card_id.toString() === next.toString()
    )[0];
    setSideToShow(!!nextSideShown);
    // let [p_correct_est, next, integralDelta_, halfLife_] = await runModel(cards, newHistory, review + 1, sideToShow, bothSides, card.card_id)
    assert(nextCards.map(c => c.card_id.toString()).includes(next.toString()), 'invalid card')   
    setIntegralDelta(0);
    setHalfLife(0);

    setPCorrect(p_correct_est);
    setReview( x => x + 1);
    setShowedTime( Date.now() );
    activeFlipOccurredRef.current = false;
    flippedTimeRef.current = null;
    setFlippedTime(null);
    setSuppressIncomingFlipTransition(true);
    setIsFront( true );
    setSessionReviews( x=>x+1);
    setTotalToday (x => x+1);
    cardRef.current = nextCard;
    setCard(nextCard);
    profiler.mark("next_card_state_committed");
    dragXY.current = [0,0];
    setShake(true);
    setTimeout(() => setShake(false), 100);
    setFlipped (false );
    await new Promise((resolve) => setTimeout(resolve, FLY_OFF_DURATION_MS));
    profiler.mark("flyoff_wait_complete");
    setFlyOff(null);
    setSuppressIncomingFlipTransition(false);
    setIsTransitioning(false);
  }

  const handleEditFieldChange = (field, value) => {
    setEditValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));
  };

  const handleUpdateCard = async () => {
    const currentCard = cardRef.current;
    if (!currentCard) {
      return;
    }

    const nextCard = {
      ...currentCard,
      front: editValues.front,
      frontAdditional: editValues.frontAdditional,
      back: editValues.back,
      backAdditional: editValues.backAdditional,
    };

    updateCardState(currentCard.card_id, nextCard);
    cardSelector.cards = cardsRef.current;

    try {
      await updateCardContent(spreadsheetAccessData, nextCard);
      setEditMode(false);
    } catch (error) {
      console.error("Failed to update card content", error);
      updateCardState(currentCard.card_id, currentCard);
      cardSelector.cards = cardsRef.current;
    }
  };
 
 
  const showNextButton = !editMode && !!card?.reverseRequired && isFront;

  return ( 
    <>
  <div className={`card flex-center${editMode ? " is-editing" : ""}`} onKeyDown={onKeyPress}>
    <div className={`do-stuff-box${editMode ? " is-editing" : ""}`} tabIndex="0" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      <div className="display">
        <StatusBar props={{
          totalToday: totalToday,
          bothSides: bothSides,
          setBothSides: handleSetBothSides,
          useRandomOrder: useRandomOrder,
          setUseRandomOrder: handleSetUseRandomOrder,
          history: history,
          cards:cards,
          card: card,
          sessionReviews: sessionReviews,
          integralDelta: integralDelta,
          halfLife: halfLife
        }}/>
        <button
          type="button"
          className="card-edit-link"
          onClick={(event) => {
            event.stopPropagation();
            setEditMode((currentValue) => !currentValue);
            setEditValues({
              front: cardRef.current?.front ?? "",
              frontAdditional: cardRef.current?.frontAdditional ?? "",
              back: cardRef.current?.back ?? "",
              backAdditional: cardRef.current?.backAdditional ?? "",
            });
          }}
        >
          Edit
        </button>
        <div className={shake ? "card-stage shake" : "card-stage"}>
          <FlipCard props={{
            flyOff: flyOff,
            flyOffDurationMs: FLY_OFF_DURATION_MS,
            disableFlipTransition: suppressIncomingFlipTransition,
            editMode: editMode,
            editValues: editValues,
            onEditFieldChange: handleEditFieldChange,
            sideToShow: sideToShow,
            dragXY: dragXY,
            isFront: isFront,
            front: card.front,
            back: card.back,
            frontAdditional: card.frontAdditional,
            backAdditional: card.backAdditional,
            onClick:flip,
            muted: !!card.muted,
            flagStatus: card.flagStatus ?? FLAG_STATUS_NONE,
            onToggleMuted: toggleMuted,
            onCycleFlagStatus: cycleFlagStatus,
          }} />
        </div>
        <div className="buttons">
          {editMode ? (
            <div className="navbutton navbutton-single navbutton-next" onClick={handleUpdateCard}>
              Update &gt;
            </div>
          ) : showNextButton ? (
            <div className="navbutton navbutton-single navbutton-next" onClick={revealReverse}>
              Next &gt;
            </div>
          ) : (
            <>
              <div className="navbutton" id="no" onClick={(e) => getCard(0, e)}>
                {params.noButtonText ?? DEFAULT_PARAMS.noButtonText}
              </div>
              <div className="navbutton" id="yes" onClick={(e) => getCard(1, e)}>
                {params.yesButtonText ?? DEFAULT_PARAMS.yesButtonText}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  </div>
  </>
  )
}
export default Card;
